import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';

interface User {
  id: number;
  email: string;
  displayName: string;
  emailVerified: boolean;
}

interface Team {
  id: number;
  name: string;
  description: string;
  role: string;
  joined_at: string;
  creator_name: string;
  member_count: number;
}

interface Invitation {
  id: number;
  team_id: number;
  team_name: string;
  team_description: string;
  invited_by_name: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  teams: Team[];
  currentTeam: Team | null;
  invitations: Invitation[];
  token: string | null;
  isLoading: boolean;
  requestVerification: (email: string) => Promise<string>;
  verifyEmail: (email: string, token: string) => Promise<void>;
  logout: () => void;
  selectTeam: (team: Team) => void;
  refreshTeams: () => Promise<void>;
  refreshInvitations: () => Promise<void>;
  acceptInvitation: (invitationId: number) => Promise<void>;
  declineInvitation: (invitationId: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [isLoading, setIsLoading] = useState(true);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(apiUrl('/api/auth/me'));
        setUser(response.data.user);
        setTeams(response.data.teams);
        
        // Set current team from localStorage or first team
        const savedTeamId = localStorage.getItem('currentTeamId');
        if (savedTeamId) {
          const savedTeam = response.data.teams.find((t: Team) => t.id.toString() === savedTeamId);
          if (savedTeam) {
            setCurrentTeam(savedTeam);
          }
        } else if (response.data.teams.length > 0) {
          setCurrentTeam(response.data.teams[0]);
        }

        // Refresh invitations
        try {
          const invitationsResponse = await axios.get(apiUrl('/api/auth/invitations'));
          setInvitations(invitationsResponse.data);
        } catch (invitationsError) {
          console.error('Failed to load invitations:', invitationsError);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestVerification = async (email: string): Promise<string> => {
    try {
      // Don't set AuthContext isLoading - let the Login component handle its own loading state
      // Setting this would cause AppContent to show loading spinner and unmount Login component
      const response = await axios.post(apiUrl('/api/auth/request-verification'), {
        email
      });
      return response.data.message;
    } catch (error: any) {
      console.error('Verification request failed:', error);
      throw new Error(error.response?.data?.error || 'Failed to send verification email');
    }
  };

  const verifyEmail = async (email: string, verificationToken: string) => {
    try {
      setIsLoading(true);
      const response = await axios.post(apiUrl('/api/auth/verify'), {
        email,
        token: verificationToken
      });

      const { token: newToken, user: userData } = response.data;
      
      // Store token in localStorage first
      localStorage.setItem('authToken', newToken);
      
      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // Set the token - this will trigger the useEffect that calls checkAuth()
      setToken(newToken);
      
      // Set user data immediately to avoid flicker
      setUser(userData);
    } catch (error: any) {
      console.error('Email verification failed:', error);
      throw new Error(error.response?.data?.error || 'Email verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setTeams([]);
    setCurrentTeam(null);
    setInvitations([]);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentTeamId');
    delete axios.defaults.headers.common['Authorization'];
  };

  const selectTeam = (team: Team) => {
    setCurrentTeam(team);
    localStorage.setItem('currentTeamId', team.id.toString());
  };

  const refreshTeams = async () => {
    if (!token) return;

    try {
      const response = await axios.get(apiUrl('/api/teams'));
      setTeams(response.data);

      // Update current team if it's still in the list
      if (currentTeam) {
        const updatedCurrentTeam = response.data.find((t: Team) => t.id === currentTeam.id);
        if (updatedCurrentTeam) {
          setCurrentTeam(updatedCurrentTeam);
        } else {
          // Current team no longer exists, select first available or none
          if (response.data.length > 0) {
            setCurrentTeam(response.data[0]);
            localStorage.setItem('currentTeamId', response.data[0].id.toString());
          } else {
            setCurrentTeam(null);
            localStorage.removeItem('currentTeamId');
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh teams:', error);
    }
  };

  const refreshInvitations = async () => {
    if (!token) return;

    try {
      const response = await axios.get(apiUrl('/api/auth/invitations'));
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to refresh invitations:', error);
    }
  };

  const acceptInvitation = async (invitationId: number) => {
    try {
      await axios.post(apiUrl(`/api/auth/invitations/${invitationId}/accept`));
      await refreshInvitations();
      await refreshTeams();
    } catch (error: any) {
      console.error('Failed to accept invitation:', error);
      throw new Error(error.response?.data?.error || 'Failed to accept invitation');
    }
  };

  const declineInvitation = async (invitationId: number) => {
    try {
      await axios.post(apiUrl(`/api/auth/invitations/${invitationId}/decline`));
      await refreshInvitations();
    } catch (error: any) {
      console.error('Failed to decline invitation:', error);
      throw new Error(error.response?.data?.error || 'Failed to decline invitation');
    }
  };

  const value: AuthContextType = {
    user,
    teams,
    currentTeam,
    invitations,
    token,
    isLoading,
    requestVerification,
    verifyEmail,
    logout,
    selectTeam,
    refreshTeams,
    refreshInvitations,
    acceptInvitation,
    declineInvitation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};